import type { ReactNode } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Handshake } from "lucide-react";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Skeleton } from "../components/ui/skeleton";
import { PageHeader } from "../components/ui/page-header";
import { BalancesBreakdown } from "../components/BalancesBreakdown";
import { useGroup, useMe } from "../api/hooks";
import { getMyGroupRole } from "../lib/groupRole";

/**
 * "What's owed" for one group (issue #453).
 *
 * The group screen used to carry a Settle up tab that mixed the transfer plan
 * with the payment history. The history now lives in the group's single activity
 * timeline, so this screen is only the forward-looking part: who should pay whom
 * to get to zero, and a way to record one. No history.
 */
export function SettleUpPage(): ReactNode {
  const { groupId } = useParams<{ groupId: string }>();
  const navigate = useNavigate();
  const groupQ = useGroup(groupId);
  const meQ = useMe();

  if (groupQ.isLoading) {
    return (
      <div className="mx-auto max-w-xl space-y-4">
        <Skeleton className="h-10 w-48" />
        <Skeleton className="h-40" />
      </div>
    );
  }

  if (!groupQ.data) {
    return (
      <Card tone="ghost">
        <CardContent className="py-12 text-center text-on-surface-variant">
          Group not found.
        </CardContent>
      </Card>
    );
  }

  const group = groupQ.data;
  const myRole = getMyGroupRole(group, meQ.data);
  const canSettle = myRole === "OWNER" || myRole === "MEMBER";

  return (
    <div className="mx-auto max-w-xl space-y-stack-lg">
      <PageHeader
        title="Settle up"
        description="The simplest set of payments that clears this group."
        back={{
          label: group.label,
          onClick: () => navigate(`/groups/${group.id}`),
        }}
      />

      <BalancesBreakdown group={group} canSettle={canSettle} />

      {canSettle ? (
        <Link to={`/groups/${group.id}/settlements/new`}>
          <Button className="w-full gap-2">
            <Handshake className="size-4" />
            Record a payment
          </Button>
        </Link>
      ) : null}
    </div>
  );
}
